import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Chip from '@material-ui/core/Chip';
import IconButton from '@material-ui/core/IconButton';
import FilterIcon from '@material-ui/icons/FilterList';
import CloseIcon from '@material-ui/icons/Close';
import CheckedIcon from '@material-ui/icons/CheckCircle';
import UncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import DefaultFilterDialog from './FilterDialog';
import {
  ARRAY_VALUE_TYPE,
  STRING_VALUE_TYPE,
  ENUM_VALUE_TYPE,
  BOOLEAN_VALUE_TYPE,
  NUMBER_VALUE_TYPE,
} from './Types';
import './FilterBar.scss';

const FilterBar = (props) => {
  const { filter, onFilterChange, filterEditor, disabled, ...rest } = props;
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [FilterDialog] = useState(() => (filterEditor.FilterDialog || DefaultFilterDialog));

  const handleDelete = (event) => {
    const defaultFilter = {};
    filterEditor.propertyEditors.forEach((s) => { defaultFilter[s.name] = s.defaultValue; });
    onFilterChange(defaultFilter);
    if (event.stopPropagation) {
      event.stopPropagation();
    }
  };

  const handleDeleteChip = cv => () => {
    const { editor } = cv;
    const editorValue = filter[editor.name];
    let newValue;
    if (editor.getDeleteValue) {
      newValue = editor.getDeleteValue();
    } else if (editor.type === ARRAY_VALUE_TYPE) {
      newValue = editorValue.filter(v => v !== cv.value);
      if (!newValue.length) {
        newValue = editor.defaultValue;
      }
    } else {
      newValue = editor.defaultValue;
    }
    onFilterChange(Object.assign({}, filter, { [cv.editor.name]: newValue }));
  };

  const getEnumValues = (editor) => {
    return Array.isArray(editor.namedValues) ? editor.namedValues : editor.namedValues(rest);
  };
  const canDeleteChip = (cv) => {
    if (cv.editor.canDeleteValue) {
      return cv.editor.canDeleteValue(cv.value, filter[cv.editor.name]);
    }
    switch (cv.editor.type) {
      case ENUM_VALUE_TYPE: return Boolean(getEnumValues(cv.editor).find(nv => nv.value === undefined));
      case BOOLEAN_VALUE_TYPE: return false; // return (cv.value !== undefined) && (cv.value !== cv.editor.defaultValue);
      case NUMBER_VALUE_TYPE: return (cv.value !== undefined) && (cv.value !== cv.editor.defaultValue);
      case STRING_VALUE_TYPE: return true;
      default: return true;
    }
  };

  const getValueLabel = (editor, value) => {
    if (editor.getValueLabel) {
      return editor.getValueLabel(value, rest);
    }
    if ((editor.type === STRING_VALUE_TYPE) || (editor.type === NUMBER_VALUE_TYPE)) {
      return `${editor.title} ${value}`;
    }
    if (editor.type === BOOLEAN_VALUE_TYPE) {
      let sv = value && 'true' || 'false';
      if ((value === undefined) && editor.undefinedValue) {
        sv = 'false';
      }
      return `${editor.title} ${sv}`;
    }
    const pair = getEnumValues(editor).find(nv => nv.value === value);
    return pair && pair.label || 'Unkown';
  };

  const getEditorChipValues = (editor) => {
    if (filter[editor.name] === undefined) return [];
    return (editor.type === ARRAY_VALUE_TYPE
      ? filter[editor.name]
      : [filter[editor.name]]).map(value => (
        { editor, value, label: getValueLabel(editor, value) }
    )).filter(cv => cv.value !== undefined);
  };
  const chipValues = filterEditor.propertyEditors
  .filter(pe => pe.editable !== false)
  .map(s => getEditorChipValues(s)).reduce(
    (ar, values) => {
      return ar.concat(values);
    }, [],
  );
  if (!chipValues.length && filterEditor.emptyChipLabel) {
    chipValues.push({
      name: '',
      label: filterEditor.emptyChipLabel,
      editor: {
        name: '',
        canDeleteValue: () => false,
      },
    });
  }

  // Handle boolean editors
  const handleCheckChip = cv => (e) => {
    const newValue = !cv.value;
    const newFilter = { ...filter };
    e.stopPropagation();
    if (newValue === cv.editor.defaultValue) {
      delete newFilter[cv.editor.name];
    } else {
      newFilter[cv.editor.name] = newValue;
    }
    onFilterChange(newFilter);
  };

  const hasCheckBox = (cv) => {
    return (cv.editor.type === BOOLEAN_VALUE_TYPE);
  };

  const handleCloseDialog = (value) => {
    setFilterDialogOpen(false);
    if (value) {
      onFilterChange(value);
    }
  };

  const handleToggleFilterDialog = () => {
    if (!disabled) {
      setFilterDialogOpen(!filterDialogOpen);
    }
  };

  const handleToggleDialogKeyDown = (event) => {
    if ((event.keyCode === 32) && !disabled) {
      setFilterDialogOpen(true);
    } else if ((event.keyCode === 27) && !disabled) {
      setFilterDialogOpen(false);
    }
  };

  return (
    <React.Fragment>
      <div
        className="grb-filterbar"
        onClick={handleToggleFilterDialog}
        onKeyDown={handleToggleDialogKeyDown}
        role="button"
        aria-label="Open filter dialog"
        tabIndex={0}
      >
        <div className="grb-filterbar-icon">
          <IconButton
            aria-label="Delete"
            size="small"
            onClick={handleToggleFilterDialog}
            disabled={disabled}
          >
            <FilterIcon />
          </IconButton>
        </div>
        <div className="grb-filterbar-icon grb-filterbar-icon-right">
          <IconButton aria-label="Delete" size="small" onClick={handleDelete} disabled={disabled}>
            <CloseIcon />
          </IconButton>
        </div>
        <div className="grb-filterbar-content">
          <div className="grb-filterbar-chips">
            {chipValues.map(cv => (
              <Chip
                key={`${cv.editor.name}${cv.label}`}
                // icon={hasCheckBox(cv) && <CheckedIcon className={`grb-filterbar-chip-checkbox ${cv.value ? 'grb-filterbar-chip-checkbox-selected' : 'grb-filterbar-chip-checkbox-unselected'}`} />}
                icon={hasCheckBox(cv) && (cv.value ? <CheckedIcon className="grb-filterbar-chip-checkbox grb-filterbar-chip-checkbox-selected" /> : <UncheckedIcon className="grb-filterbar-chip-checkbox" />) || undefined}
                onClick={hasCheckBox(cv) ? handleCheckChip(cv) : undefined}
                label={cv.label}
                onDelete={canDeleteChip(cv) ? handleDeleteChip(cv) : undefined}
              />
            ))}
          </div>
        </div>
      </div>
      {filterDialogOpen && (
        <FilterDialog
          {...rest}
          value={filter}
          filterEditor={filterEditor}
          onClose={handleCloseDialog}
          open
        />
      )}
    </React.Fragment>
  );
};

FilterBar.propTypes = {
  filter: PropTypes.shape({}).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  filterEditor: PropTypes.shape({
    FilterDialog: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({}),
    ]),
    propertyEditors: PropTypes.arrayOf(PropTypes.shape({})),
    emptyChipLabel: PropTypes.string,
  }).isRequired,
  disabled: PropTypes.bool,
};

FilterBar.defaultProps = {
  disabled: false,
};

export default FilterBar;
